<template>
	<div class="characteristic-desire-experience">
		<slot name="info-text"></slot>
		<div class="mt-3">
			<RadioGroup
				v-model="type"
				:options="typeOptions"
				@input="typeChanged"
			/>
		</div>
		<div v-if="typeIsYes" class="my-3">
			<slot name="yes-text"></slot>
		</div>
		<div v-if="typeIsIrrelevant" class="my-3">
			<slot name="irrelevant-text"></slot>
		</div>
		<div v-if="type && experienceEligible" class="characteristic-desire-experience--items">
			<div
				v-for="characteristic in characteristicItems"
				:key="characteristic.id"
				class="characteristic-desire-experience--item"
			>
				<b-row align-v="center">
					<b-col md="5">
						<template v-if="typeIsNo">
							<Label :characteristic="characteristic"/>
							<span
								v-if="characteristic.description"
								class="characteristic-desire-experience--item-description"
								v-b-tooltip.html
								:title="characteristic.description"
							>
								<Icon icon="info" />
							</span>
						</template>
						<template v-else>
							<b-form-checkbox
								:checked="isDesired(characteristic)"
								@change="desireChanged(characteristic, $event)"
							>
								<Label :characteristic="characteristic"/>
								<span
									v-if="characteristic.description"
									class="characteristic-desire-experience--item-description"
									v-b-tooltip.html
									:title="characteristic.description"
								>
									<Icon icon="info" />
								</span>
							</b-form-checkbox>
						</template>
					</b-col>
					<b-col md="7" class="mt-2 mt-md-0">
						<Select
							:value="getExperience(characteristic)"
							:options="experienceOptions"
							empty-value-label="Bitte triff eine Auswahl, ob du bereits Erfahrung hast"
							smaller
							@input="experienceChanged(characteristic, $event)"
						/>
					</b-col>
				</b-row>
			</div>
		</div>
	</div>
</template>

<script>
import Label from "@/vue/characteristic/Label";
import _ from "lodash";
import {isDefined} from "@/helper/Value";
import {mapState} from "vuex";
import Select from "@/vue/common/form/Select";
import SingleSelect from "@/vue/characteristic/SingleSelect";
import {arrayIsNotEmpty} from "@/helper/Array";
import RadioGroup from "@/vue/common/form/RadioGroup.vue";
import Icon from "@/vue/icon/Icon.vue";
import Scale from "@/vue/characteristic/Scale.vue";

const TYPE__YES = 'yes';
const TYPE__NO = 'no';
const TYPE__IRRELEVANT = 'irrelevant';

export default {

	components: {
		Scale,
		RadioGroup,
		SingleSelect,
		Select,
		Label,
		Icon,
	},

	props: {
		characteristicItems: {
			type: Array,
			required: true,
		},
	},

	data ()
	{
		return {
			type: undefined,
			selected: [],
		};
	},

	computed: {
		...mapState('meta', ['meta']),
		experienceOptions ()
		{
			return _.map(this.meta.workExperience, characteristicItem =>
			{
				return {
					value: characteristicItem,
					text: characteristicItem.label
				};
			});
		},
		typeOptions ()
		{
			return [
				{value: TYPE__IRRELEVANT, text: 'Egal'},
				{value: TYPE__YES, text: 'Ja'},
				{value: TYPE__NO, text: 'Nein'},
			];
		},
		typeIsYes ()
		{
			return this.type === TYPE__YES;
		},
		typeIsNo ()
		{
			return this.type === TYPE__NO;
		},
		typeIsIrrelevant ()
		{
			return this.type === TYPE__IRRELEVANT;
		},
		typeValid ()
		{
			return isDefined(this.type);
		},
		desiredValid ()
		{
			if (!this.typeIsYes)
			{
				return true;
			}

			const desiredItems = _.filter(this.selected, item => item.desired);

			return arrayIsNotEmpty(desiredItems);
		},
		experienceEligible ()
		{
			return this.typeValid && !this.typeIsNo;
		},
		valid ()
		{
			return this.typeValid && this.desiredValid;
		},
	},

	methods: {
		typeChanged (type)
		{
			if (type === TYPE__IRRELEVANT)
			{
				this.selected = _.map(this.characteristicItems, characteristic =>
				{
					let alreadySelectedItem = this.getSelection(characteristic);

					return {
						item: characteristic,
						desired: true,
						experience: alreadySelectedItem?.experience
					}
				});
			}

			this.emitChangeEvent();
		},
		desireChanged (characteristic, checked)
		{
			let item = this.getSelection(characteristic);

			if (!item)
			{
				item = {
					item: characteristic
				};

				this.selected.push(item);
			}

			this.$set(item, 'desired', checked);

			const desiredItems = _.filter(this.selected, item => item.desired);

			// not all desired any longer, switch to "yes"
			if (this.typeIsIrrelevant && desiredItems.length !== this.characteristicItems.length)
			{
				this.type = TYPE__YES;
			}

			this.emitChangeEvent();
		},
		isDesired (characteristic)
		{
			return this.getSelection(characteristic)?.desired;
		},
		getSelection (characteristic)
		{
			return _.find(this.selected, item => item.item.id === characteristic.id);
		},
		getExperience (characteristic)
		{
			return this.getSelection(characteristic)?.experience;
		},
		experienceChanged (characteristic, experience)
		{
			let item = this.getSelection(characteristic);

			if (!item)
			{
				item = {
					item: characteristic
				};

				this.selected.push(item);
			}

			this.$set(item, 'desired', true);
			this.$set(item, 'experience', experience);

			this.emitChangeEvent();
		},
		emitChangeEvent ()
		{
			if (!this.valid)
			{
				this.$emit('input', undefined);
				return;
			}

			this.$emit('input', {
				none: this.typeIsNo
					? true
					: undefined,
				desired: this.typeIsYes
					? _.filter(this.selected, item => item.desired)
					: [],
				experience: this.experienceEligible
					? _.filter(this.selected, item => isDefined(item.experience))
					: [],
			});
		}
	}
}
</script>
